import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useBreadcrumb } from '@/app/Breadcrumb'
import { ME_CONTEXT_QUERY_KEY } from '@/app/MeContextProvider'
import { Card, EmptyState, PageHeader, Tag, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'

import type { AdminModule } from '../api'
import { adminApi, adminKeys } from '../api'
import { EnvModuleState, VisibilityCheckbox } from '../components/Toggles'

const ENV_HINT = 'Konfiguracja w zmiennych środowiskowych (MODULES_ENABLED)'

/** Kolumny ról bierzemy z pierwszego modułu — backend zwraca tę samą listę dla wszystkich. */
function roleColumns(modules: AdminModule[]) {
  return modules[0]?.visibility ?? []
}

export function ModulesPage() {
  useBreadcrumb(['Administracja', 'Moduły i widoczność'])
  const toast = useToast()
  const queryClient = useQueryClient()

  const modulesQuery = useQuery({ queryKey: adminKeys.modules, queryFn: adminApi.modules })
  const modules = modulesQuery.data ?? []

  // Zapis natychmiastowy: każda zmiana checkboxa = jedno PUT (bez przycisku „Zapisz").
  const saveMutation = useMutation({
    mutationFn: ({
      moduleKey,
      roleId,
      visible,
    }: {
      moduleKey: string
      roleId: number
      visible: boolean
    }) => adminApi.saveVisibility(moduleKey, [{ role_id: roleId, visible }]),
    onSuccess: (updated) => {
      queryClient.setQueryData<AdminModule[]>(adminKeys.modules, (current) =>
        (current ?? []).map((module) => (module.key === updated.key ? updated : module)),
      )
      void queryClient.invalidateQueries({ queryKey: ME_CONTEXT_QUERY_KEY })
      toast.success('Zapisano widoczność', updated.name)
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.modules })
      toast.error(
        'Nie zapisano widoczności',
        error instanceof ApiError ? error.userMessage : undefined,
      )
    },
  })

  const columns = roleColumns(modules)

  return (
    <>
      <PageHeader
        title="Moduły i widoczność"
        subtitle="Administrator decyduje, które role widzą poszczególne moduły — te zmiany zapisują się od razu. O tym, czy moduł działa w kancelarii, rozstrzyga konfiguracja instalacji; tutaj widać wyłącznie jej stan."
      />

      <Card className="overflow-hidden">
        {modulesQuery.isPending ? (
          <EmptyState title="Wczytywanie modułów…" />
        ) : modules.length === 0 ? (
          <EmptyState title="Brak modułów" description="Rejestr nie zwrócił żadnego modułu." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-line-soft">
                  <th scope="col" className="t-label px-5 py-3 text-left font-semibold">
                    Moduł
                  </th>
                  <th scope="col" className="px-4 py-3 text-left align-bottom">
                    <span className="t-label font-semibold">W kancelarii</span>
                    <span className="t-small mt-0.5 block font-normal normal-case tracking-normal text-muted">
                      tylko odczyt · <code className="font-mono">MODULES_ENABLED</code>
                    </span>
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.role_id}
                      scope="col"
                      className="t-label px-4 py-3 text-center font-semibold"
                    >
                      {column.role_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => (
                  <tr
                    key={module.key}
                    className={cn(
                      'border-b border-line-soft last:border-0',
                      !module.enabled && 'bg-[#fbfcfd]',
                    )}
                  >
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-base font-semibold',
                            module.enabled ? 'text-ink' : 'text-muted',
                          )}
                        >
                          {module.name}
                        </span>
                        {module.core ? <Tag>RDZEŃ</Tag> : null}
                      </div>
                      <p className="t-small mt-0.5 text-muted">{module.description}</p>
                    </td>
                    <td className="px-4 py-3.5 align-middle">
                      <EnvModuleState on={module.enabled} label={module.name} />
                    </td>
                    {module.visibility.map((cell) => (
                      <td key={cell.role_id} className="px-4 py-3.5 text-center align-middle">
                        <VisibilityCheckbox
                          label={`${module.name} — widoczny dla roli ${cell.role_name}`}
                          checked={cell.visible}
                          disabled={!module.enabled || cell.locked || saveMutation.isPending}
                          title={
                            !module.enabled
                              ? ENV_HINT
                              : cell.locked
                                ? 'Rola z uprawnieniami administracyjnymi musi widzieć moduł Administracja'
                                : undefined
                          }
                          onChange={(visible) =>
                            saveMutation.mutate({
                              moduleKey: module.key,
                              roleId: cell.role_id,
                              visible,
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="t-small mt-3 text-muted">
        Moduł niewłączony w instalacji ma zablokowaną także widoczność — najpierw trzeba dopisać
        go do <code className="font-mono">MODULES_ENABLED</code> i zrestartować aplikację.
      </p>

      {/* Sekcja „Wpływ zmian" (mockup, prawa kolumna pod tabelą) — planowana na v2. */}
    </>
  )
}
